import React from "react";
import {
  UpOutlined,
  DownOutlined,
  LeftOutlined,
  RightOutlined,
  PlusOutlined,
  MinusOutlined,
} from "@ant-design/icons";
import { easeOut } from "ol/easing";
import { COLORS } from "../../../../consts";

/**
 * Pan / zoom pad for an OpenLayers map.
 * Pass the live `map` instance as the `map` prop.
 */
export default function MapNavigator({ map, panPixels = 160, zoomStep = 0.5 }) {
  if (!map) return null;

  /* ---------- helpers ---------- */
  const pan = (dx, dy) => {
    const view = map.getView();
    const centre = map.getPixelFromCoordinate(view.getCenter());
    const dest = map.getCoordinateFromPixel([centre[0] + dx, centre[1] + dy]);
    view.animate({ center: dest, duration: 400, easing: easeOut });
  };

  const zoomIn = () =>
    map.getView().animate({
      zoom: map.getView().getZoom() + zoomStep,
      duration: 400,
      easing: easeOut,
    });

  const zoomOut = () =>
    map.getView().animate({
      zoom: map.getView().getZoom() - zoomStep,
      duration: 400,
      easing: easeOut,
    });

  /* ---------- shared btn style ---------- */
  const btnStyle = {
    width: 32,
    height: 32,
    border: "none",
    background: "transparent",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    cursor: "pointer",
    color: COLORS.darkerGray,
    fontSize: 22,
    lineHeight: "32px",
    userSelect: "none",
    borderRadius: 4,
    transition:
      "background 0.18s ease, color 0.18s ease, transform 0.18s ease, box-shadow 0.18s ease",
  };

  /* ---------- render ---------- */
  return (
    <>
      <style>
        {`
          .map-nav-btn:hover,
          .map-nav-btn:focus-visible {
            background: ${COLORS.primaryBlue}15;
            color: ${COLORS.primaryBlue} !important;
            transform: scale(1.12);
            box-shadow: 0 2px 6px rgba(0,0,0,.25);
            outline: none;
          }
        `}
      </style>

      <div
        style={{
          position: "absolute",
          bottom: 20,
          right: 20,
          zIndex: 1000,

          background: "rgba(255,255,255,0.8)",
          border: "1px solid #eee",
          borderRadius: 8,
          boxShadow: "0 1px 4px rgba(0,0,0,0.25)",
          padding: 6,

          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* --- D-pad (Up row then Left/Down/Right row) --- */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, 32px)",
            gridTemplateRows: "repeat(2, 32px)",
          }}
        >
          <div />
          <button
            style={btnStyle}
            className="map-nav-btn"
            title="Pan up"
            onClick={() => pan(0, -panPixels)}
          >
            <UpOutlined />
          </button>
          <div />
          <button
            style={btnStyle}
            className="map-nav-btn"
            title="Pan left"
            onClick={() => pan(-panPixels, 0)}
          >
            <LeftOutlined />
          </button>
          <div /> {/* centre dummy */}
          <button
            style={btnStyle}
            className="map-nav-btn"
            title="Pan right"
            onClick={() => pan(panPixels, 0)}
          >
            <RightOutlined />
          </button>
          <div />
          <button
            style={btnStyle}
            className="map-nav-btn"
            title="Pan down"
            onClick={() => pan(0, panPixels)}
          >
            <DownOutlined />
          </button>
          <div />
        </div>

        {/* --- zoom controls (side-by-side) --- */}
        <div style={{ marginTop: 6, display: "flex", gap: 6 }}>
          <button
            style={btnStyle}
            className="map-nav-btn"
            title="Zoom in"
            onClick={zoomIn}
          >
            <PlusOutlined />
          </button>
          <button
            style={btnStyle}
            className="map-nav-btn"
            title="Zoom out"
            onClick={zoomOut}
          >
            <MinusOutlined />
          </button>
        </div>
      </div>
    </>
  );
}
